import React, { FC, useEffect, useState } from 'react'

import { PencilIcon } from 'lucide-react'

import { Tooltip, TooltipTrigger, TooltipContent } from '../../../../../ui/tooltip'
import { RequiredFieldInput } from '../../../../../../pages/competitor/clinicAndOther/Tabs/components/RequiredFieldInput'

import { cn } from '../../../../../../lib/utils'
import { reqFieldLabel, reqFieldValue } from '../../../../../../helpers/requiredFields'

import { IOption } from '../../../../../inputs/InputNewMemberRow'
import {
  IProfileReqField,
  IReqFieldType,
} from '../../../../../../pages/competitor/clinicAndOther/Tabs/components/ProfileFields'

interface CardItemProps {
  fieldType: IReqFieldType
  fieldKey?: string
  label: string
  defaultValue: string | number | null
  url?: string
  isInvalid?: boolean
  isMissing?: boolean
  isExpired?: boolean
  field?: IProfileReqField

  isEdit?: boolean
  showLabelError?: boolean
  onChange?: (state: {
    fieldName: string
    fieldType: IReqFieldType
    fieldKey?: string
    option?: IOption
  }) => void
}

export const CardItem: FC<CardItemProps> = ({
  label,
  field,
  url,
  defaultValue,
  fieldType,
  fieldKey,
  isInvalid,
  isMissing,
  isExpired,
  isEdit,
  showLabelError,
  onChange,
}) => {
  const [value, setValue] = useState<IOption>({
    value: '',
    label: '',
  })

  useEffect(() => {
    if (field) {
      setValue({
        label: reqFieldLabel(field),
        value: reqFieldValue(field),
      })
    } else {
      setValue({
        value: String(defaultValue || ''),
        label: String(defaultValue || ''),
      })
    }
  }, [defaultValue, field])

  return (
    <li className="min-h-[56px] py-3 sm:py-1 text-base flex flex-wrap flex-col md:flex-row items-center gap-2 justify-between md:text-right">
      <p className="w-full md:w-auto text-black/50">{label}</p>
      <div className="ml-auto flex w-full md:w-auto md:flex-[1_0_auto] flex-wrap justify-end gap-3 items-center">
        {showLabelError && !isEdit && (
          <div className="flex items-center gap-2">
            {isMissing ? (
              <p className="text-sm py-1 px-4 rounded-full text-black bg-black/5">Missing</p>
            ) : isExpired || isInvalid ? (
              <p className="text-sm py-1 px-4 rounded-full text-[#E80000] bg-[#E80000]/5">
                {isExpired ? 'Expired' : isInvalid && 'Invalid'}
              </p>
            ) : (
              <p className="text-sm py-1 px-4 rounded-full text-[#00B6AA] bg-[#00B6AA]/10">Valid</p>
            )}
          </div>
        )}
        {defaultValue &&
          (!isEdit ||
            (fieldType !== 'text' &&
              fieldType !== 'tel' &&
              fieldType !== 'date' &&
              fieldType !== 'select')) &&
          (fieldType === 'document' || fieldType === 'number' || fieldType === 'paperwork' ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={url}
                  target="_blank"
                  className={cn(
                    url ? 'underline hover:no-underline underline-offset-2' : 'pointer-events-auto'
                  )}
                  rel="noreferrer"
                  onClick={(e) => !url && e.preventDefault()}
                >
                  {defaultValue ?? 'No record found'}
                </a>
              </TooltipTrigger>
              <TooltipContent>
                <p>{url ? 'View document' : 'No record found'}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <p>{defaultValue}</p>
          ))}
        {isEdit && (
          <>
            {fieldType === 'tel' ||
            fieldType === 'text' ||
            fieldType === 'select' ||
            fieldType === 'date' ? (
              <div className="w-full flex-[1_0_auto] max-w-[200px]">
                <RequiredFieldInput
                  fieldKey={fieldKey || ''}
                  fieldType={fieldType}
                  value={value}
                  onChange={(option) =>
                    onChange &&
                    onChange({
                      fieldName: label,
                      fieldKey,
                      fieldType,
                      option,
                    })
                  }
                />
              </div>
            ) : (
              <button
                onClick={() => onChange && onChange({ fieldName: label, fieldKey, fieldType })}
                className="underline opacity-70 hover:no-underline hover:opacity-100"
              >
                <PencilIcon className="w-4" />
              </button>
            )}
          </>
        )}
      </div>
    </li>
  )
}
